import { useCallback, useEffect, useRef, useState } from "react";
import { Loader2, Send, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { getSupportChatUserUUID } from "./getSupportChatUserUUID";

const SUPPORT_CHAT_WELCOME_MESSAGE =
  "How can we help? If you don't see a response within a few minutes, please email kosh@kennedyryon.com or text (470) 916-7966.";

type SupportChatMessage = {
  _id: string;
  body: string;
  received: boolean;
  createdAt: string;
};

export function SupportChatOverlay({
  open,
  onOpenChange,
  productId,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  productId: string;
}) {
  const [messages, setMessages] = useState<SupportChatMessage[]>([]);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [replyText, setReplyText] = useState("");
  const [error, setError] = useState<string | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const userUUIDRef = useRef(getSupportChatUserUUID());

  const loadMessages = useCallback(
    async ({ initial = false }: { initial?: boolean } = {}) => {
      if (!productId) return;

      if (initial) {
        setLoading(true);
        setError(null);
      }

      try {
        const params = new URLSearchParams({
          userUUID: userUUIDRef.current,
          productId,
        });
        const response = await fetch(`/api/support-chat?${params}`);
        const data = (await response.json().catch(() => ({}))) as {
          error?: string;
          messages?: SupportChatMessage[];
        };

        if (!response.ok) {
          throw new Error(data.error ?? "Failed to load support chat");
        }

        setMessages(data.messages ?? []);
      } catch (e) {
        if (initial) {
          setError(
            e instanceof Error ? e.message : "Failed to load support chat",
          );
          setMessages([]);
        }
      } finally {
        if (initial) {
          setLoading(false);
        }
      }
    },
    [productId],
  );

  useEffect(() => {
    if (!open) return;
    void loadMessages({ initial: true });
  }, [open, loadMessages]);

  useEffect(() => {
    if (!open) return;

    const intervalId = window.setInterval(() => {
      void loadMessages();
    }, 5000);

    return () => {
      window.clearInterval(intervalId);
    };
  }, [open, loadMessages]);

  useEffect(() => {
    if (!open) return;
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, open]);

  async function handleSend() {
    const body = replyText.trim();
    if (!body || sending || !productId) return;

    setSending(true);
    setError(null);
    try {
      const response = await fetch("/api/support-chat", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          userUUID: userUUIDRef.current,
          productId,
          body,
        }),
      });
      const data = (await response.json().catch(() => ({}))) as {
        error?: string;
        message?: SupportChatMessage;
      };

      if (!response.ok) {
        throw new Error(data.error ?? "Failed to send message");
      }

      if (data.message) {
        setMessages((current) => [...current, data.message!]);
      } else {
        await loadMessages();
      }
      setReplyText("");
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to send message");
    } finally {
      setSending(false);
    }
  }

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-end p-4 sm:p-6 pointer-events-none">
      <button
        type="button"
        aria-label="Close support chat"
        className="absolute inset-0 bg-black/50 pointer-events-auto"
        onClick={() => onOpenChange(false)}
      />
      <div className="relative z-10 flex h-[min(32rem,calc(100dvh-2rem))] w-full max-w-md flex-col overflow-hidden rounded-2xl border border-white/10 bg-zinc-950 text-white shadow-2xl pointer-events-auto">
        <div className="flex items-center justify-between border-b border-white/10 px-4 py-3">
          <div>
            <h2 className="text-sm font-semibold">Support</h2>
          </div>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="text-gray-400 hover:text-white hover:bg-white/10"
            onClick={() => onOpenChange(false)}
          >
            <X className="size-4" />
          </Button>
        </div>

        <div className="flex-1 overflow-y-auto px-4 py-4 space-y-3 min-h-0">
          {loading && messages.length === 0 ? (
            <div className="flex justify-center py-12">
              <Loader2 className="size-6 animate-spin text-gray-400" />
            </div>
          ) : (
            <>
              <div className="flex justify-start">
                <div className="max-w-[85%] rounded-2xl px-3 py-2 text-sm whitespace-pre-wrap bg-white/10 text-white">
                  {SUPPORT_CHAT_WELCOME_MESSAGE}
                </div>
              </div>
              {messages.map((message) => (
                <div
                  key={message._id}
                  className={`flex ${message.received ? "justify-end" : "justify-start"}`}
                >
                  <div
                    className={`max-w-[85%] rounded-2xl px-3 py-2 text-sm whitespace-pre-wrap ${
                      message.received
                        ? "bg-primary text-primary-foreground"
                        : "bg-white/10 text-white"
                    }`}
                  >
                    {message.body}
                  </div>
                </div>
              ))}
            </>
          )}
          <div ref={messagesEndRef} />
        </div>

        {error ? (
          <p className="px-4 pb-2 text-xs text-red-400">{error}</p>
        ) : null}

        <div className="border-t border-white/10 p-4">
          <div className="flex items-end gap-2">
            <Textarea
              value={replyText}
              onChange={(e) => setReplyText(e.target.value)}
              placeholder="Type your message…"
              rows={2}
              className="min-h-[3rem] resize-none border-white/10 bg-white/5 text-white placeholder:text-gray-500"
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  void handleSend();
                }
              }}
            />
            <Button
              type="button"
              size="icon"
              disabled={!replyText.trim() || sending}
              onClick={() => void handleSend()}
            >
              {sending ? (
                <Loader2 className="size-4 animate-spin" />
              ) : (
                <Send className="size-4" />
              )}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
